import { Box, IconButton, styled, useTheme } from "@material-ui/core";
import { AnimatePresence, motion, Variants } from "framer-motion";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@material-ui/icons/Close";
import { getVideoMediaType } from "../../utils";
import { useKeyPressEvent, useLockBodyScroll } from "react-use";

export const Backdrop = styled(motion.div)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: theme.palette.common.black,
  zIndex: 1200,
  overflow: "hidden",
  cursor: "pointer"
}));

export const CustomVideo = styled("video")(({ theme }) => ({
  width: "100%",
  maxHeight: "min(100%, 1920px)",
  outline: 0,
  maxWidth: "1920px"
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(4),
  height: theme.spacing(4),
  backgroundColor: "#333",
  padding: 0,
  "&:hover": {
    backgroundColor: "#444"
  },
  "&:active": {
    transform: "scale(1.2)"
  },
  "& svg": {
    fill: theme.palette.grey[300]
  }
}));

export const backdropAnimations: Variants = {
  initial: {
    opacity: 0,
    scale: 0
  },
  open: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.25
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  }
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  videoSrc: string;
  videoFallbackSrc?: string;
};

export default function FullscreenVideo({
  isOpen,
  onClose,
  videoSrc,
  videoFallbackSrc
}: Props) {
  const theme = useTheme();

  // Render backdrop at root
  const [portalRoot, setPortalRoot] = useState<HTMLElement>(null);
  useEffect(() => {
    setPortalRoot(document.getElementById("___gatsby"));
  }, []);

  useLockBodyScroll(isOpen); // Lock body scroll when open
  useKeyPressEvent("Escape", onClose); // Close video when user press escape key

  if (portalRoot) {
    return createPortal(
      <AnimatePresence>
        {isOpen && (
          <Backdrop
            animate={isOpen ? "open" : "initial"}
            exit="exit"
            variants={backdropAnimations}
            onClick={onClose}
          >
            <CustomVideo
              controls
              autoPlay
              playsInline
              onClick={e => e.stopPropagation()}
            >
              <source src={videoSrc} type={getVideoMediaType(videoSrc)} />
              {videoFallbackSrc && (
                <source
                  src={videoFallbackSrc}
                  type={getVideoMediaType(videoFallbackSrc)}
                />
              )}
            </CustomVideo>
            <Box
              position="absolute"
              top={theme.spacing(2)}
              right={theme.spacing(2)}
            >
              <CloseButton title={"Close video"}>
                <CloseIcon />
              </CloseButton>
            </Box>
          </Backdrop>
        )}
      </AnimatePresence>,
      portalRoot
    );
  }

  return null;
}
